import { create } from 'zustand';
import apiClient from '../lib/api';

export interface Meeting {
    id: string;
    tenantId: string;
    title: string;
    description?: string;
    startTime: string;
    endTime: string;
    location?: string;
    meetingUrl?: string;
    status: string;
    createdAt: string;
    updatedAt: string;
}

interface MeetingState {
    meetings: Meeting[];
    isLoading: boolean;
    error: string | null;
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
    fetchMeetings: (filters?: any) => Promise<void>;
    createMeeting: (data: Partial<Meeting>) => Promise<void>;
    updateMeeting: (id: string, data: Partial<Meeting>) => Promise<void>;
    deleteMeeting: (id: string) => Promise<void>;
}

export const useMeetingStore = create<MeetingState>((set, get) => ({
    meetings: [],
    isLoading: false,
    error: null,
    pagination: {
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0,
    },
    fetchMeetings: async (filters?: any) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.get('/work/meetings', { params: filters });
            const { data, pagination } = response.data;
            set({ meetings: data, pagination, isLoading: false });
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to fetch meetings', isLoading: false });
        }
    },
    createMeeting: async (data: Partial<Meeting>) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.post('/work/meetings', data);
            const newMeeting = response.data;
            set((state) => ({
                meetings: [newMeeting, ...state.meetings].sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()),
                isLoading: false,
            }));
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to create meeting', isLoading: false });
            throw error;
        }
    },
    updateMeeting: async (id: string, data: Partial<Meeting>) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.patch(`/work/meetings/${id}`, data);
            const updatedMeeting = response.data;
            set((state) => ({
                meetings: state.meetings.map((o) => (o.id === id ? updatedMeeting : o)),
                isLoading: false,
            }));
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to update meeting', isLoading: false });
            throw error;
        }
    },
    deleteMeeting: async (id: string) => {
        set({ error: null });
        try {
            await apiClient.delete(`/work/meetings/${id}`);
            set((state) => ({ meetings: state.meetings.filter((m) => m.id !== id) }));
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to delete meeting' });
            throw error;
        }
    },
}));
